import { useEffect, useRef, useState } from "react";
import SidebarToggle from "../../Doctor/SidebarToggle";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchOrdersOfDoctor,
} from "../../../redux/Thunk/Order/OrderThunks";
import { buttonData } from "../../../utils/constants";
import Pagination from "../../common/Pagination";
import toast from "react-hot-toast";
import Loader from "../../Loader";
import UPIPaymentModal from "./UPIPaymentModal";
import ConfirmationModal from "./ConfirmationModal";
import apiAxios from "../../../services/apiAxios";
import {
  fetchDoctorQuickOrders,
  cancelQuickOrder,
} from "../../../redux/Thunk/QuickOrder/QuickOrderThunks";

const TrackingComp = ({ doctorId }) => {
  const [selected, setSelected] = useState("all");
  const [openOrderId, setOpenOrderId] = useState(null);
  const [paymentModalInfo, setPaymentModalInfo] = useState({ isOpen: false, orderId: null, amount: 0 });
  const [confirmModalState, setConfirmModalState] = useState({ isOpen: false, orderId: null });
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState("all");
  const [limit] = useState(10); // Fixed limit for number of items per page
  const loading = useSelector((state) => state?.order?.loading);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const dispatch = useDispatch();

  // Quick Order state
  const quickOrders = useSelector((state) => state.quickOrder?.quickOrders || []);
  const [qoCancelTarget, setQoCancelTarget] = useState(null);
  const [qoCancelReason, setQoCancelReason] = useState("");

  const { orders } = useSelector(
    ({ order }) => ({
      orders: order?.orders,
    }),
    shallowEqual
  );
  const isTrack = useSelector((state) => state.order.isTrack);
  const currentPageRef = useRef(currentPage);
  const filterTypeRef = useRef(filterType);
  const ordersRef = useRef(orders);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    filterTypeRef.current = filterType;
  }, [filterType]);

  useEffect(() => {
    dispatch(fetchOrdersOfDoctor({ page: currentPage, limit: 10, filterType }));
  }, [dispatch, currentPage, limit, filterType]);

  // Fetch Quick Orders on mount
  useEffect(() => {
    dispatch(fetchDoctorQuickOrders());
  }, [dispatch]);

  const handleButtonClick = (value) => {
    setSelected(value);
  };



  useEffect(() => {
    if (selected?.length > 0) {
      setFilterType(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (orders) {
      setFilteredOrders(orders?.orders);
      ordersRef.current = orders;
    }
  }, [orders]);

  useEffect(() => {
    if (selected?.length > 0) {
      setCurrentPage(1);
    }
  }, [selected]);

  const toggleOrder = (OID) => {
    if (openOrderId === OID) {
      setOpenOrderId(null);
    } else {
      setOpenOrderId(OID);
    }
  };


  const finalHandleConfirmCancel = async () => {
    const OID = orderToCancel;
    const reason = cancelReason;

    if (!reason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      const cancelledReason = { cancelReason: reason };
      const response = await apiAxios.patch(`/api/order/cancel/${OID}`, cancelledReason);

      if (response.status === 200 || response.data?.message) {
        setFilteredOrders((prevOrders) =>
          prevOrders?.map((order) =>
            order.OID === OID ? { ...order, isCancelled: true, cancelReason: reason } : order
          )
        );
        setOrderToCancel(null);
        setCancelReason("");
        toast.success("Order cancelled successfully.");
      }
    } catch (error) {
      console.error("Complete Error Object:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel the order.");
    }
  };

  const executeMarkAsPaid = async () => {
    const orderId = confirmModalState.orderId;
    setConfirmModalState({ isOpen: false, orderId: null });

    try {
      const res = await apiAxios.patch(`/api/order/mark-paid/${orderId}`);
      if (res.status === 200) {
        toast.success("Order marked as paid successfully!");
        // Update local state without fetching again for faster UX
        setFilteredOrders((prevOrders) =>
          prevOrders?.map((order) =>
            order.OID === orderId ? { ...order, isPaid: true } : order
          )
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to mark order as paid.");
    }
  };


  const totalPages = orders?.totalPages || 1;
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return { time: "", date: "" };
    const date = new Date(timeString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { time, date: `${day}/${month}/${year}` };
  };

  // Robust Real-time Updates: Listener for FCM Notifications
  useEffect(() => {
    if (isTrack?.OID) {
      const { OID, DID } = isTrack || {};

      const isOurOrder = ordersRef.current?.orders?.some(
        (order) => order?.OID === OID
      );

      if ((DID && DID === doctorId) || (!DID && isOurOrder)) {
        dispatch(
          fetchOrdersOfDoctor({
            page: currentPageRef.current,
            limit: 10,
            filterType: filterTypeRef.current,
            background: true,
          })
        );
      }
    }
  }, [isTrack, doctorId, dispatch]);

  // Polling Fallback: Refresh every 15 seconds to catch updates if FCM fails
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        dispatch(
          fetchOrdersOfDoctor({
            page: currentPageRef.current,
            limit: 10,
            filterType: filterTypeRef.current,
            background: true,
          })
        );
      }
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [dispatch, currentPage]);

  const getStatusIndex = (order) => {
    if (order.isCancelled) return -1;
    if (order.isDelivered || order.isCollected) return 4;
    if (order.isDispatched || (order.isCollect && order.isPacked)) return 3; // Ready/Dispatched
    if (order.isPacked) return 2;
    if (order.isAccepted) return 1;
    return 0; // Placed
  };

  const renderProgressBar = (order) => {
    const steps = [
      { label: "Placed", time: order.createdAt },
      { label: "Accepted", time: order.acceptTime },
      { label: "Packed", time: order.packedTime },
      { label: order.isCollect ? "Ready" : "Dispatched", time: order.isCollect ? order.packedTime : order.dispatchTime },
      { label: order.isCollect ? "Collected" : "Delivered", time: order.isCollect ? order.collectedTime : order.deliveredTime },
    ];
    const currentIndex = getStatusIndex(order);

    if (order.isCancelled) {
      return (
        <div className="flex items-center gap-3 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100">
          <span className="font-bold uppercase tracking-wide">Cancelled</span>
          <span className="text-sm font-medium">{order.cancelReason}</span>
        </div>
      );
    }

    const progressPercentage = Math.max(0, Math.min(100, (currentIndex / (steps.length - 1)) * 100));

    return (
      <div className="relative pt-6 pb-0">
        {/* Track */}
        <div className="absolute top-10 left-0 right-0 h-1.5 bg-gray-100 rounded-full"></div>
        {/* Progress limit */}
        <div className="absolute top-10 left-0 h-1.5 bg-primary-600 rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }}></div>

        {/* Cursor */}
        <div className="absolute transition-all duration-500 z-10" style={{ left: `calc(${progressPercentage}% - 32px)`, top: '-14px' }}>
          <img src="/delivery-boy.svg" alt="delivery" className="w-16 h-16 min-w-[64px] min-h-[64px] shrink-0 transform scale-x-[-1] drop-shadow-lg" />
        </div>

        {/* Points */}
        <div className="relative flex justify-between mt-10 md:mt-12">
          {steps.map((step, idx) => {
            const isActive = idx <= currentIndex;
            return (
              <div key={idx} className="flex flex-col items-center gap-1 w-1/5 text-center px-0.5">
                <span className={`text-[9px] sm:text-[12px] font-bold leading-tight ${isActive ? 'text-primary-600' : 'text-gray-400'}`}>{step.label}</span>
                {isActive && step.time && (
                  <span className="text-[8px] md:text-[10px] text-gray-500 font-medium">{formatTime(step.time).time}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="tracking-wrapper">
      <Loader loading={loading} />
      <div className="flex items-center justify-between flex-wrap md:flex-nowrap gap-y-4 gap-x-6 mb-4 md:mb-8">
        <div className="flex items-center gap-3 flex-shrink-0">
          <SidebarToggle />

          {/* Brand Logo & Wordmark + Page Name */}
          <div className="flex flex-col gap-1 shrink-0 justify-center">
            <div className="flex items-center gap-1.5 shrink-0">
              <img src="/logo.png" alt="logo" className="h-[18px] sm:h-[22px] w-auto object-contain shrink-0" />
              <img src="/logo.svg" alt="Hyperapt" className="h-[15px] sm:h-[18px] w-auto max-w-[90px] sm:max-w-[105px] object-contain shrink-0" />
            </div>
            <span className="text-[17px] sm:text-[21px] font-black text-gray-900 tracking-tight leading-none">
              Tracking
            </span>
          </div>

          {/* Vertical Partition */}
          <div className="h-7 w-[1px] bg-gray-200 hidden sm:block"></div>

          <div className="flex items-center gap-2 md:gap-2.5">
            <div className="flex items-center gap-1.5 px-2 py-0.5 md:px-2.5 md:py-1 bg-green-50 text-green-600 rounded-full border border-green-100 animate-pulse shadow-sm shadow-green-50/50">
              <span className="w-1 md:w-1.5 h-1 md:h-1.5 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
              <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-widest leading-none">Live</span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-auto overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex bg-gray-50 p-1 rounded-[12px] md:rounded-[16px] w-fit items-center gap-1 border border-gray-100 shadow-sm">
            {buttonData.map((btn) => (
              <button
                key={btn.value}
                onClick={() => handleButtonClick(btn.value)}
                className={`px-3 md:px-5 py-2 md:py-2.5 rounded-[10px] md:rounded-[12px] font-bold text-[11px] md:text-[13px] whitespace-nowrap transition-all ${selected === btn.value
                  ? "bg-white text-primary-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                  }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Orders Section — Only shows pending quick orders */}
      {quickOrders?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <h3 className="text-[13px] font-bold text-orange-600 uppercase tracking-wider">Quick Orders</h3>
            <span className="text-[10px] font-bold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">{quickOrders.length} pending</span>
          </div>
          {quickOrders.map((qo) => (
            <div
              key={qo.QID}
              className="mb-3 border border-orange-200 bg-gradient-to-br from-orange-50/50 to-amber-50/30 p-4 rounded-[16px] shadow-sm transition-all duration-300 hover:shadow-md hover:ring-1 hover:ring-orange-300"
            >
              {/* QO Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[15px] font-extrabold text-orange-700">{qo.QID}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest border bg-orange-100 text-orange-600 border-orange-200">
                    Quick Order
                  </span>
                  <span className="text-[10px] font-medium text-gray-400">
                    {formatTime(qo.createdAt || Date.now()).date}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => window.open(qo.qoUploadUrl, '_blank')}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors"
                  >
                    Open Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => setQoCancelTarget(qo.QID)}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Patient Info */}
              <div className="flex items-center gap-4 text-[11px] text-gray-600">
                <span className="font-semibold text-gray-800">
                  {qo.patientDetail?.name || ''} {qo.patientDetail?.surname || ''}
                </span>
                {qo.patientDetail?.phoneNumber && (
                  <span className="text-gray-500">{qo.patientDetail.phoneNumber}</span>
                )}
                {qo.patientDetail?.gender && (
                  <span className="text-gray-400">{qo.patientDetail.gender}</span>
                )}
                {qo.patientDetail?.age && (
                  <span className="text-gray-400">{qo.patientDetail.age} yrs</span>
                )}
              </div>

              {/* Payment & Delivery Methods Info */}
              <div className="flex items-center gap-3 mt-2 text-[10px] uppercase font-bold tracking-wider">
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100/50">
                  Payment: {qo.paymentMethod === 'upi' ? 'UPI' : qo.paymentMethod === 'cash' ? 'Cash' : qo.paymentMethod === 'card' ? 'Card' : qo.paymentMethod || 'UPI'}
                </span>
                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-100/50">
                  Delivery: {qo.deliveryMethod === 'isClinic' ? 'Clinic' : qo.deliveryMethod === 'isCollect' ? 'Collect' : qo.deliveryMethod === 'isAddress' ? 'Address' : qo.deliveryMethod || 'Clinic'}
                </span>
              </div>

              {/* Optional Order Notes */}
              {qo.qoNotes && (
                <div className="mt-2.5 p-2 bg-white/70 rounded-xl border border-orange-100/50 text-[11px] text-gray-600 shadow-inner">
                  <span className="font-bold text-orange-600/80 block uppercase tracking-wider text-[9px] mb-0.5">Order Notes:</span>
                  <p className="whitespace-pre-line leading-relaxed">{qo.qoNotes}</p>
                </div>
              )}

              {/* Pending status bar */}
              <div className="mt-3 flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <span className="text-[11px] font-bold text-amber-700">Awaiting Store</span>
                <span className="text-[10px] text-amber-500 ml-auto">Prescription uploaded — waiting for store to create order</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Order Cancel Modal */}
      {qoCancelTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm transition-all">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Quick Order</h3>
            <p className="text-sm text-gray-500 mb-3">QID: <strong>{qoCancelTarget}</strong></p>
            <textarea
              value={qoCancelReason}
              onChange={(e) => setQoCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-100 resize-none"
              rows={3}
            />
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => { setQoCancelTarget(null); setQoCancelReason(''); }}
                className="flex-1 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={async () => {
                  if (!qoCancelReason.trim()) {
                    toast.error('Please provide a reason');
                    return;
                  }
                  await dispatch(cancelQuickOrder({ qid: qoCancelTarget, cancelReason: qoCancelReason }));
                  setQoCancelTarget(null);
                  setQoCancelReason('');
                }}
                className="flex-1 py-2 rounded-xl text-sm font-bold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Cancel Quick Order
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        {filteredOrders?.length ? (
          filteredOrders?.map((order) => {
            const {
              OID,
              XID,
              QID,
              patientDetail,
              billing,
              payment,
              isPacked,
              isCancelled,
              isClinic,
              isCollect,
              isAddress,
              invoice,
              createdAt,
              contact,
              isPaid
            } = order;

            const orderType = isClinic ? "Clinic" : isCollect ? "Collect" : isAddress ? "Address" : "Order";

            const returnAmt = billing?.totalReturnAmount || (order?.orderProducts || []).filter(p => p.isReturn).reduce((acc, rp) => {
              const price = rp?.netPrice || rp?.price || rp?.mrp || 0;
              const qty = rp?.orderQty || rp?.quantity || 1;
              const discountPercent = rp?.discount || 0;
              const itemTotal = price * qty;
              const itemDiscount = (itemTotal * discountPercent) / 100;
              return acc + (itemTotal - itemDiscount);
            }, 0);

            return (
              <div
                key={OID}
                className="mb-4 md:mb-6 break-inside-avoid border border-gray-200 p-3 md:p-5 rounded-[16px] md:rounded-[20px] bg-white shadow-sm hover:shadow-lg hover:ring-1 hover:ring-primary-600 transition-shadow duration-300"
              >
                <div className="relative mb-2 pb-3 md:pb-5 border-b border-gray-100">
                  <div className="flex flex-col gap-3 md:gap-4 w-full pr-0 md:pr-8">
                    {/* Row 1: Header & Billing */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 border-b border-gray-100 pb-3 md:pb-4">
                      {/* Left: OID Delivery type date */}
                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        <h2 className="text-[16px] md:text-[18px] font-extrabold text-gray-900 leading-none truncate">
                          {OID}
                        </h2>
                        <span className="flex items-center gap-1 text-[9px] md:text-[10px] px-2 py-0.5 md:px-2.5 md:py-1 rounded-full font-bold uppercase tracking-widest border bg-primary-50 text-primary-600 border-primary-100 shadow-sm">
                          {orderType}
                        </span>
                        <span className="text-[10px] md:text-[11px] font-medium text-gray-400">
                          {formatTime(createdAt || Date.now()).date}
                        </span>
                      </div>

                      {/* Right: Amount Block, Payment Method, Actions */}
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full sm:w-auto">
                        {/* Amount Block */}
                        {!isPacked ? (
                          <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="text-[12px] md:text-[13px] font-extrabold text-gray-900 tracking-tight">₹{billing?.payableAmount || 0}</span>
                            <span className="text-[10px] md:text-[11px] text-gray-400 line-through hidden md:inline">₹{billing?.subTotal || 0}</span>
                            <span className="text-[10px] md:text-[11px] font-bold text-green-600">₹{billing?.totalDiscount || 0} off</span>
                            {returnAmt > 0 && (
                              <span className="text-[10px] md:text-[11px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">
                                -₹{Number(returnAmt).toFixed(2)} return
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="text-[12px] md:text-[13px] font-extrabold text-gray-900 tracking-tight">₹{invoice?.invoiceAmount || billing?.payableAmount || 0}</span>
                            <span className="text-[10px] md:text-[11px] font-bold text-green-600">₹{billing?.totalDiscount || 0} off</span>
                            {returnAmt > 0 && (
                              <span className="text-[10px] md:text-[11px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">
                                -₹{Number(returnAmt).toFixed(2)} return
                              </span>
                            )}
                          </div>
                        )}

                        {/* Payment Method */}
                        <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 border border-gray-200 rounded-lg bg-white">
                          <span className="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Pay</span>
                          <span className="text-[10px] md:text-[11px] font-bold text-primary-600 uppercase">{isPacked ? invoice?.paymentType : payment}</span>
                        </div>

                        {/* Make/Mark payment buttons */}
                        {isPacked && !isCancelled && (
                          <div className="flex items-center gap-2 ml-auto sm:ml-0">
                            {isPaid ? (
                              <span className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-green-50 text-green-700 font-bold rounded-[8px] text-[10px] md:text-[11px] border border-green-200 shadow-sm">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                Paid
                              </span>
                            ) : (
                              <div className="flex gap-2 items-center">
                                <button
                                  type="button"
                                  onClick={() => setPaymentModalInfo({ isOpen: true, orderId: OID, amount: (invoice?.invoiceAmount || billing?.payableAmount || 0) })}
                                  className="px-4 md:px-10 py-1 md:py-1.5 bg-green-50 hover:bg-green-400 hover:text-green-900 text-green-700 border border-green-400 font-bold rounded-[8px] text-[10px] md:text-[11px] transition-all shadow-md active:scale-95"
                                >
                                  Pay UPI
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Cancel Order Inline Button */}
                        {!isCancelled && !isPacked && !order?.isDelivered && !order?.isDispatched && !isCollect && !isAddress && (
                          <button
                            type="button"
                            onClick={() => setOrderToCancel(OID)}
                            className="flex items-center justify-center w-[28px] h-[28px] md:w-[30px] md:h-[30px] bg-white hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-[8px] transition-all border border-gray-200 shadow-sm ml-auto sm:ml-0"
                            title="Cancel Order"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Patient & Reference */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      {/* Left: Patient Details */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-6">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Patient</span>
                          <span className="text-[13px] md:text-[14px] font-bold text-gray-800 leading-tight">
                            {patientDetail?.name} {patientDetail?.surname}
                          </span>
                        </div>
                        <div className="w-[1px] h-6 bg-gray-100 hidden md:block"></div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Contact</span>
                          <span className="text-[12px] md:text-[13px] font-bold text-gray-600 tracking-tight">
                            {patientDetail?.phoneNumber || contact?.contactNo || "N/A"}
                          </span>
                        </div>
                        <div className="w-[1px] h-6 bg-gray-100 hidden md:block"></div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Ref XID</span>
                          <span className="text-[12px] md:text-[13px] font-bold text-gray-600 font-mono tracking-tight bg-gray-50 px-2 py-0.5 rounded border border-gray-100 w-fit">
                            {XID || "N/A"}
                          </span>
                        </div>
                        {QID && (
                          <>
                            <div className="w-[1px] h-6 bg-gray-100 hidden md:block"></div>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[8px] md:text-[9px] font-bold text-orange-400 uppercase tracking-widest">Ref QID</span>
                              <span className="text-[12px] md:text-[13px] font-bold text-orange-600 font-mono tracking-tight bg-orange-50 px-2 py-0.5 rounded border border-orange-100 w-fit flex items-center gap-1">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                                {QID}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Right: View Medicines Toggle */}
                      <div className="w-full md:w-auto">
                        <button
                          type="button"
                          onClick={() => toggleOrder(OID)}
                          className="w-full md:w-auto px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-600 font-bold rounded-[10px] text-[11px] md:text-[12px] transition-colors border border-primary-100 shadow-sm flex items-center justify-center gap-2"
                        >
                          View Medicines
                        </button>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Progress Bar */}
                <div className="mt-6 mb-0 px-2">
                  {renderProgressBar(order)}
                </div>
              </div>
            );
          })
        ) : (
          <div className="py-8 md:py-12 bg-gray-50 rounded-2xl border border-gray-100 text-center">
            <p className="text-gray-400 font-semibold text-base md:text-lg">No orders found</p>
          </div>
        )}
      </div>

      {/* Medicine Modal (Absolute Popup) */}
      {openOrderId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-2xl w-[95%] max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col transform transition-all">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="font-extrabold text-gray-800 text-lg">Ordered Medicines</h3>
              <button onClick={() => setOpenOrderId(null)} className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {filteredOrders.find(o => o.OID === openOrderId)?.orderProducts?.map((medicine, index) => (
                <div key={index} className={`flex justify-between items-center p-4 border rounded-[14px] shadow-sm transition-colors ${
                  medicine?.isReturn ? "bg-rose-50/50 border-rose-200 hover:border-rose-300" : "bg-white border-gray-100 hover:border-primary-200"
                }`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-800 text-[16px]">{medicine?.productName}</h4>
                      {medicine?.isReturn ? (
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest bg-rose-100 text-rose-700 border border-rose-200">
                          Returned Product
                        </span>
                      ) : medicine?.isCustom ? (
                        <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">
                          Custom
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[13px] text-gray-500 mt-1.5 font-medium flex items-center flex-wrap gap-y-1">
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2 shrink-0">{medicine?.type}</span>
                      {!medicine?.isReturn && (
                        <span className="text-gray-700">
                          Dosage:{" "}
                          {medicine?.dosageMode === "SOS" || medicine?.isSOS
                            ? `SOS (As Needed)${medicine?.takenTime ? ` • ${medicine.takenTime}` : ''} • ${medicine?.rxDaysDisplay || (medicine?.rxDays ? `${medicine.rxDays} Days` : '1 Day')}`
                            : medicine?.dosageMode === "NA" || medicine?.isNA
                            ? `NA (Not Applicable)`
                            : medicine?.dosageMode === "PERIODIC" || medicine?.isPeriodic
                            ? `${medicine?.periodicFrequency || "Periodic"} • ${medicine?.morningTime || medicine?.midDay || medicine?.eveningTime || medicine?.night || 1} qty/dose${medicine?.takenTime ? ` • ${medicine.takenTime}` : ''} • ${medicine?.rxDaysDisplay || (medicine?.rxDays ? `${medicine.rxDays} Days` : '')}`
                            : medicine?.dosageMode === "REDUCING" || medicine?.isReducingDose
                            ? `Reducing Schedule (${(medicine?.reducingDoses || []).length} steps)${medicine?.takenTime ? ` • ${medicine.takenTime}` : ''} • ${medicine?.rxDaysDisplay || (medicine?.rxDays ? `${medicine.rxDays} Days` : '')}`
                            : `${medicine?.morningTime || 0}-${medicine?.midDay || 0}-${medicine?.eveningTime || 0}-${medicine?.night || 0} • ${medicine?.takenTime} • ${medicine?.rxDaysDisplay || (medicine?.rxDays ? `${medicine.rxDays} Days` : '')}`}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right pl-4">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest block mb-1">Qty</span>
                    <span className="font-black text-primary-600 text-2xl">{medicine?.orderQty}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
              <button onClick={() => setOpenOrderId(null)} className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white rounded-[12px] font-bold transition-all shadow-md active:scale-95 text-sm">
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPI Payment Modal */}
      <UPIPaymentModal
        isOpen={paymentModalInfo.isOpen}
        amount={paymentModalInfo.amount}
        orderId={paymentModalInfo.orderId}
        onClose={() => setPaymentModalInfo({ isOpen: false, orderId: null, amount: 0 })}
        onPaymentSuccess={() => {
          setFilteredOrders((prevOrders) =>
            prevOrders?.map((order) =>
              order.OID === paymentModalInfo.orderId ? { ...order, isPaid: true } : order
            )
          );
        }}
      />

      {/* Cancel Order Modal */}
      {orderToCancel && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-[2px] transition-all animate-in fade-in duration-300">
          <div className="bg-white rounded-[24px] w-[95%] max-w-[440px] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 border border-gray-100">
            <div className="px-8 pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </div>
              <h3 className="text-[22px] font-bold text-gray-900 mb-2">Cancel Order?</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed">
                Please provide a reason to cancel <b>Order #{orderToCancel}</b>. This action cannot be undone.
              </p>
            </div>

            <div className="px-8 pb-8">
              <div className="mb-6">
                <label className="block text-[12px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Cancellation Reason</label>
                <textarea
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-red-500 transition-all outline-none text-[15px] resize-none"
                  rows="3"
                  placeholder="e.g. Out of stock, Patient requested..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setOrderToCancel(null);
                    setCancelReason("");
                  }}
                  className="flex-1 h-12 text-[15px] font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={finalHandleConfirmCancel}
                  className="flex-1 h-12 text-[15px] font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-lg shadow-red-100 active:scale-[0.98]"
                >
                  Cancel Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Mark as Paid Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModalState.isOpen}
        title="Mark Order as Paid?"
        message={`This correctly overrides the current payment intent limits locking the order to Paid status for further dispatches. You cannot undo this easily.`}
        onConfirm={executeMarkAsPaid}
        onCancel={() => setConfirmModalState({ isOpen: false, orderId: null })}
      />

      {filteredOrders?.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isPreviousDisabled={currentPage === 1}
          isNextDisabled={currentPage === totalPages}
          itemLength={filteredOrders?.length}
          totalItems={orders?.totalItems}
        />
      )}
    </div>
  );
};

export default TrackingComp;
